import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useBlocker } from 'react-router-dom'
import { useSWRConfig, type SWRResponse } from 'swr'
import {
  Background,
  BackgroundVariant,
  Controls,
  MarkerType,
  ReactFlow,
  type Edge,
  type EdgeChange,
  type NodeChange,
  type NodeTypes,
} from '@xyflow/react'
import { Button } from '@ludus/ui'
import type { PipelineId } from '@ludus/core'
import {
  fromGraph,
  isLoop,
  nodeRole,
  unreachableNodeIds,
  validateFlowGraph,
  type NodeBranch,
} from '@/features/flows/types.ts'
import { FlowApiError, saveFlowGraph, type FlowDetail, type FlowTrigger } from '@/lib/api/flows.ts'
import { apiErrorMessage } from '@/shared/feedback/apiErrorMessage.ts'
import { CanvasToolbar } from './CanvasToolbar.tsx'
import { FlowNodeCard, type FlowCanvasNode } from './FlowNodeCard.tsx'
import { CELL_X, CELL_Y, sameCell, toCell, toPixels } from './grid.ts'
import { useFlowEditorState } from './useFlowEditorState.ts'
import { NodeDrawer } from '@/features/flows/NodeDrawer/NodeDrawer.tsx'
import { useNodeLabels } from './useNodeLabels.ts'
import '@xyflow/react/dist/style.css'

/**
 * The flow editor (D9, §9 Q14) — the one recorded exception to the repo's
 * hand-rolled-primitives stance, and it lives here rather than in
 * `packages/ui` because nothing else needs a canvas yet.
 *
 * **The staged graph is the only model.** xyflow's nodes and edges are derived
 * from it on every render and nothing is stored twice: positions come out of
 * `graph.nodes[].position` in grid cells, and arrows come out of
 * `resolve`/`reject` through core's `fromGraph`. A canvas that kept its own
 * copy of either would eventually answer "where does this branch go" twice,
 * and one of the two answers would be stale — the reason `FlowEdge` is derived
 * in the first place.
 *
 * **What the canvas refuses, and what it merely marks.** A drop that closes a
 * loop is refused outright, at the drop, because a cyclic graph cannot be
 * stored at all and accepting it would hand back a canvas that has to be
 * repaired before it can be saved. Every other refusal — an unconfigured
 * trigger, a second parent — is *marked*: core's `validateFlowGraph` runs on
 * the draft, the count sits in the toolbar and the nodes it names are ringed.
 * Those are states a person passes through on the way to the graph they mean.
 *
 * **Save and Enable stay two gestures** (D2). This screen saves; the switch on
 * the pipeline settings page enables. A save that also enabled would make
 * "store what I have so far" impossible.
 */

const NODE_TYPES: NodeTypes = { flow: FlowNodeCard }

interface CanvasProps {
  pipelineId: PipelineId
  flow: FlowDetail
  /** The page's own read, so a save populates the cache the page renders from. */
  flowSwr: SWRResponse<{ flow: FlowDetail; triggers: FlowTrigger[] }, FlowApiError>
  /**
   * The nodes a selected run touched (#176). Drawn as the path that run took,
   * so "what happened" and "what is configured" are the same picture.
   *
   * Owned by the page rather than by either panel: the runs list decides which
   * run is open, the canvas only draws what it is handed, and neither reaches
   * into the other.
   */
  highlight?: readonly string[]
}

export function Canvas({ pipelineId, flow, flowSwr, highlight }: CanvasProps) {
  const { t } = useTranslation()
  const { mutate } = useSWRConfig()

  const draft = useFlowEditorState((s) => s.draft)
  const dirty = useFlowEditorState((s) => s.dirty)
  const selectedNodeId = useFlowEditorState((s) => s.selectedNodeId)
  const pending = useFlowEditorState((s) => s.pending)
  const notice = useFlowEditorState((s) => s.notice)
  const open = useFlowEditorState((s) => s.open)
  const reset = useFlowEditorState((s) => s.reset)
  const close = useFlowEditorState((s) => s.close)
  const baseVersion = useFlowEditorState((s) => s.baseVersion)
  const markSaved = useFlowEditorState((s) => s.markSaved)
  const select = useFlowEditorState((s) => s.select)
  const moveNode = useFlowEditorState((s) => s.moveNode)
  const connect = useFlowEditorState((s) => s.connect)
  const disconnect = useFlowEditorState((s) => s.disconnect)
  const deleteNode = useFlowEditorState((s) => s.deleteNode)
  const clearNotice = useFlowEditorState((s) => s.clearNotice)
  const disarm = useFlowEditorState((s) => s.disarm)

  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<FlowApiError | null>(null)

  // Seeded from the SWR row, once per flow. `open` is a no-op while the store
  // already holds this flow, so a background revalidation cannot delete edits.
  // The version travels with the graph: the save's compare-and-swap must cite
  // the version this draft was built on, not whatever the cache holds later.
  useEffect(
    () => open(flow.id, flow.graph, flow.version),
    [open, flow.id, flow.graph, flow.version],
  )

  // The store is a module singleton, so without this the staged graph outlives
  // the route: leaving a flow and coming back would re-render the old draft
  // (and `open` would no-op on it, since the flow id still matches) instead of
  // what is stored. Leaving is already guarded by `useBlocker` below, so a
  // person who gets here has answered for the unsaved edits.
  useEffect(() => () => close(), [close])

  const labels = useNodeLabels(pipelineId, draft)

  const issues = useMemo(
    () => (draft === null ? [] : validateFlowGraph(draft, { pipelineId })),
    [draft, pipelineId],
  )
  const issuesByNode = useMemo(() => {
    const counts = new Map<string, number>()
    for (const issue of issues) {
      if (issue.nodeId === undefined) continue
      counts.set(issue.nodeId, (counts.get(issue.nodeId) ?? 0) + 1)
    }
    return counts
  }, [issues])

  const unreachable = useMemo(
    () => new Set(draft === null ? [] : unreachableNodeIds(draft)),
    [draft],
  )

  // Who is pointed at, regardless of whether the role rules allow it. A card
  // uses this to draw an entrance the rules withhold, so an illegal pointer at
  // a trigger (`trigger_is_targeted`) stays visible and removable instead of
  // being dropped from the render — the graph has to be repairable in the
  // editor that flagged it.
  const targeted = useMemo(() => {
    const ids = new Set<string>()
    if (draft === null) return ids
    for (const n of draft.nodes) {
      if (n.resolve !== null) ids.add(n.resolve)
      if (n.reject !== null) ids.add(n.reject)
    }
    return ids
  }, [draft])

  const highlighted = useMemo(() => new Set(highlight ?? []), [highlight])

  const nodes: FlowCanvasNode[] = useMemo(() => {
    if (draft === null) return []
    return draft.nodes.map((node) => ({
      id: node.id,
      type: 'flow' as const,
      position: toPixels(node.position),
      selected: node.id === selectedNodeId,
      // Refused here as well as in the store: the Delete key reaches xyflow's
      // own handler, which never asks the store's opinion.
      deletable: nodeRole(node.type) !== 'trigger',
      data: {
        node,
        unreachable: unreachable.has(node.id),
        issues: issuesByNode.get(node.id) ?? 0,
        labels,
        targeted: targeted.has(node.id),
        onRunPath: highlighted.has(node.id),
      },
    }))
  }, [draft, selectedNodeId, unreachable, issuesByNode, labels, targeted, highlighted])

  const edges: Edge[] = useMemo(() => {
    if (draft === null) return []
    return fromGraph(draft).edges.map((edge) => ({
      id: edge.id,
      source: edge.source,
      target: edge.target,
      sourceHandle: edge.branch,
      targetHandle: 'in',
      // Bezier, not orthogonal: a branch handle sits low on its card and the
      // entrance sits mid-height on the next one, so a right-angle route leaves
      // a hook at every source. A curve reads as one arrow between two points.
      type: 'default',
      data: { branch: edge.branch },
      markerEnd: { type: MarkerType.ArrowClosed },
      // An edge is on the run's path when both of its ends are. The steps
      // record nodes, not arrows, so the path between them is derived rather
      // than stored — and deriving it is exact here, because a run walks one
      // chain and an edge joining two visited nodes is an edge it took.
      animated: highlighted.has(edge.source) && highlighted.has(edge.target),
      style:
        highlighted.has(edge.source) && highlighted.has(edge.target)
          ? { stroke: 'var(--color-primary)', strokeWidth: 2 }
          : edge.branch === 'reject'
            ? { strokeDasharray: '6 4', stroke: 'var(--color-muted)' }
            : { stroke: 'var(--color-accent)' },
    }))
  }, [draft, highlighted])

  const onNodesChange = useCallback(
    (changes: NodeChange<FlowCanvasNode>[]): void => {
      // Selection is resolved from the whole batch rather than change by change.
      // xyflow emits a click on a new node as [select new, deselect old] in
      // `nodes` order, so the deselect can arrive *after* the select; handling
      // each against the render-time `selectedNodeId` then let the trailing
      // deselect clear the node the click had just chosen, leaving nothing
      // selected and Duplicate/Delete disabled right after a click on a card.
      let selectionTouched = false
      let picked: string | null = null
      let clearedCurrent = false

      for (const change of changes) {
        if (change.type === 'position' && change.position !== undefined) {
          const node = draft?.nodes.find((n) => n.id === change.id)
          const cell = toCell(change.position)
          if (node !== undefined && !sameCell(node.position, cell)) moveNode(change.id, cell)
        } else if (change.type === 'select') {
          selectionTouched = true
          if (change.selected) picked = change.id
          else if (change.id === selectedNodeId) clearedCurrent = true
        } else if (change.type === 'remove') {
          deleteNode(change.id)
        }
      }

      if (!selectionTouched) return
      if (picked !== null) select(picked)
      else if (clearedCurrent) select(null)
    },
    [draft, moveNode, select, selectedNodeId, deleteNode],
  )

  // Only removals are acted on: an arrow is a reading of a `resolve`/`reject`
  // pointer, so deleting one clears the pointer and everything else about it is
  // recomputed from the graph on the next render.
  const onEdgesChange = useCallback(
    (changes: EdgeChange<Edge>[]): void => {
      for (const change of changes) {
        if (change.type !== 'remove') continue
        const edge = edges.find((e) => e.id === change.id)
        if (edge?.sourceHandle) disconnect(edge.source, edge.sourceHandle as NodeBranch)
      }
    },
    [edges, disconnect],
  )

  async function save(): Promise<void> {
    if (draft === null) return
    setSaving(true)
    setSaveError(null)
    try {
      const saved = await saveFlowGraph(pipelineId, flow.id, {
        graph: draft,
        version: baseVersion ?? flow.version,
      })
      markSaved(flow.id, draft, saved.version)
      // The saved row plus the graph that produced it, written straight into
      // the page's cache. `revalidate: false` because the answer *is* the
      // truth; the triggers the engine re-projected are carried over from the
      // previous read and go stale until the next one, which nothing on this
      // screen renders — the runs panel (#176) is the first thing that will.
      await flowSwr.mutate(
        (prev) => ({ flow: { ...saved, graph: draft }, triggers: prev?.triggers ?? [] }),
        { revalidate: false },
      )
    } catch (err) {
      setSaveError(
        err instanceof FlowApiError
          ? err
          : new FlowApiError({ kind: 'unknown', status: null, message: 'save failed' }),
      )
    } finally {
      setSaving(false)
    }

    // Outside the `try`, and after `saving` is cleared, on purpose: SWR's global
    // `mutate` revalidates and rejects on a fetch error, so refreshing the
    // catalogue inside the save's own error path would report a save that
    // landed as a save that failed — with Save then disabled, because `dirty`
    // is already false. The same shape as the `FlowCard.toggle` defect fixed in
    // #264 (§10.2 lesson 5).
    await mutate(`/pipelines/${pipelineId}/flows`).catch(() => undefined)
  }

  /** The 409's only sound answer: re-read, and start again from what is stored. */
  async function reload(): Promise<void> {
    setSaveError(null)
    const fresh = await flowSwr.mutate()
    if (fresh !== undefined) reset(flow.id, fresh.flow.graph, fresh.flow.version)
  }

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      dirty && currentLocation.pathname !== nextLocation.pathname,
  )

  // The tab's own exit. `useBlocker` cannot see a reload or a close, so the two
  // guards are separate and neither replaces the other.
  useEffect(() => {
    if (!dirty) return undefined
    const warn = (event: BeforeUnloadEvent): void => event.preventDefault()
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [dirty])

  if (draft === null) return null

  // The drawer's subject is the canvas's own selection rather than a second
  // piece of state: a node is open in the drawer exactly when it is selected,
  // so clicking another card moves the drawer with it and the two can never
  // disagree about what is being edited.
  const selectedNode = draft.nodes.find((n) => n.id === selectedNodeId) ?? null

  return (
    <div className="@container/flow flex min-h-[32rem] flex-1 flex-col">
      <CanvasToolbar
        draft={draft}
        dirty={dirty}
        saving={saving}
        issues={issues.length}
        onSave={() => void save()}
        onDiscard={() => reset(flow.id, flow.graph, flow.version)}
      />

      {notice !== null && (
        <Banner
          tone="warning"
          text={t(`pages.flows.canvas.notice.${notice}`, { defaultValue: notice })}
          action={{
            label: t('pages.flows.canvas.dismiss', { defaultValue: 'Dismiss' }),
            onClick: clearNotice,
          }}
        />
      )}

      {pending !== null && (
        <Banner
          tone="info"
          text={t('pages.flows.canvas.notice.pending', {
            defaultValue: 'Pick the node this branch should go to, then press Enter on it.',
          })}
        />
      )}

      {saveError !== null && (
        <Banner
          tone="error"
          text={
            saveError.kind === 'conflict'
              ? t('pages.flows.canvas.conflict', {
                  defaultValue:
                    'This flow was saved elsewhere while you were editing. Reload to start from the stored version.',
                })
              : saveError.kind === 'invalid_graph'
                ? t('pages.flows.canvas.refused', {
                    count: saveError.issues.length,
                    defaultValue: 'The engine refused this graph: {{count}} problem(s).',
                  })
                : apiErrorMessage(saveError, t, 'pages.flows.canvas')
          }
          action={
            saveError.kind === 'conflict'
              ? {
                  label: t('pages.flows.canvas.reload', { defaultValue: 'Reload' }),
                  onClick: () => void reload(),
                }
              : undefined
          }
        />
      )}

      {blocker.state === 'blocked' && (
        <Banner
          tone="warning"
          text={t('pages.flows.canvas.leave', {
            defaultValue: 'This flow has unsaved changes.',
          })}
          action={{
            label: t('pages.flows.canvas.leaveAnyway', { defaultValue: 'Leave anyway' }),
            onClick: () => blocker.proceed(),
          }}
          secondary={{
            label: t('pages.flows.canvas.stay', { defaultValue: 'Stay' }),
            onClick: () => blocker.reset(),
          }}
        />
      )}

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-b-xl border border-border bg-background @lg/flow:flex-row">
        <div className="min-h-0 min-w-0 flex-1">
          <ReactFlow<FlowCanvasNode>
            nodes={nodes}
            edges={edges}
            nodeTypes={NODE_TYPES}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={(connection) => {
              if (connection.source === null || connection.target === null) return
              connect(
                connection.source,
                (connection.sourceHandle ?? 'resolve') as NodeBranch,
                connection.target,
              )
            }}
            isValidConnection={(connection) =>
              connection.source !== null &&
              connection.target !== null &&
              !isLoop(draft, connection.source, connection.target)
            }
            onPaneClick={() => {
              // Both: `select(null)` clears the selection and the notice, but an
              // armed connection is neither. Left alone it stays live behind a
              // banner with no dismiss, and completes on the next Enter over any
              // node — silently rewiring a branch the person had abandoned.
              select(null)
              disarm()
            }}
            // Explicit, because @xyflow/react defaults `deleteKeyCode` to
            // 'Backspace' alone — the comment on `deletable` above claims the
            // Delete key reaches this handler, and without naming it, it does not.
            deleteKeyCode={['Delete', 'Backspace']}
            snapToGrid
            snapGrid={[CELL_X, CELL_Y]}
            fitView
            fitViewOptions={{ padding: 0.2, maxZoom: 1 }}
            minZoom={0.3}
            maxZoom={1.5}
            proOptions={{ hideAttribution: false }}
          >
            <Background variant={BackgroundVariant.Dots} gap={[CELL_X / 4, CELL_Y / 4]} size={1} />
            <Controls showInteractive={false} />
          </ReactFlow>
        </div>

        {selectedNode !== null && (
          <NodeDrawer
            key={selectedNode.id}
            node={selectedNode}
            graph={draft}
            pipelineId={pipelineId}
            onClose={() => select(null)}
          />
        )}
      </div>
    </div>
  )
}

interface BannerAction {
  label: string
  onClick: () => void
}

const BANNER_TONE = {
  info: 'bg-toast-info text-toast-info-text',
  warning: 'bg-toast-warning text-toast-warning-text',
  error: 'bg-toast-error text-toast-error-text',
} as const

function Banner({
  tone,
  text,
  action,
  secondary,
}: {
  tone: keyof typeof BANNER_TONE
  text: string
  action?: BannerAction
  secondary?: BannerAction
}) {
  return (
    <div
      role="status"
      className={`flex flex-wrap items-center gap-2 border-x border-border px-3 py-2 text-xs ${BANNER_TONE[tone]}`}
    >
      <span className="min-w-0 flex-1">{text}</span>
      {secondary && (
        <Button variant="ghost" size="sm" className="text-xs" onClick={secondary.onClick}>
          {secondary.label}
        </Button>
      )}
      {action && (
        <Button variant="secondary" size="sm" className="text-xs" onClick={action.onClick}>
          {action.label}
        </Button>
      )}
    </div>
  )
}
